<template lang="html">
    <div class="hydrologic-variability-climate-table">
        <h2>{{ title }}</h2>
        <b-table
            :data="data"
            :is-fullwidth="true"
            :narrowed="true"
            :row-class="(row, index) => `row-${index}`"
        >
            <template slot-scope="props">
                <b-table-column
                    field="station_number"
                    label="Watershed"
                >
                    <span v-if="props.row.station_number === 'Ungauged basin'">
                        {{ watershedName }}
                    </span>
                    <span v-else>
                        <div class="candidate-number">
                            <span
                                class="dot"
                                :style="{ 'background-color': colorScheme[props.index] }"
                            />
                            {{ props.row.station_number }}
                        </div>
                        <div class="station-name is-size-7">
                            {{ props.row.station_name }}
                        </div>
                    </span>
                </b-table-column>
                <b-table-column
                    field="lat"
                    label="Location (lat, long)"
                >
                    <template v-slot:header>
                        <div class="has-text-right">
                            Location<br>
                            <span class="has-text-weight-light has-text-grey">(lat, long)</span>
                        </div>
                    </template>
                    {{ props.row.lat | numFormat('0.000') }},<br>{{ props.row.lng | numFormat('0.000') }}
                </b-table-column>
                <b-table-column
                    field="area_km2"
                    label="Area (km²)"
                >
                    <template v-slot:header>
                        <div class="has-text-right">
                            Area<br>
                            <span class="has-text-weight-light has-text-grey">(km²)</span>
                        </div>
                    </template>
                    {{ props.row.area_km2 | numAutoFormat }}
                </b-table-column>
                <b-table-column
                    field="min_elev"
                    label="Elevation (m: min, mean, max)"
                >
                    <template v-slot:header>
                        <div class="has-text-right">
                            Elevation<br>
                            <div class="has-text-weight-light has-text-grey ">
                                (m: min,<br>mean, max)
                            </div>
                        </div>
                    </template>
                    {{ props.row.min_elev | numFormat('0,0') }},<br>
                    {{ props.row.avg_elev | numFormat('0,0') }},<br>
                    {{ props.row.max_elev | numFormat('0,0') }}
                </b-table-column>
                <b-table-column
                    field="ppt"
                    label="Precipitation (mm/mo)"
                    centered
                    class="chart-column"
                >
                    <template v-slot:header>
                        <div>
                            Precipitation<br>
                            <span class="has-text-weight-light has-text-grey">(mm/mo)</span>
                        </div>
                    </template>
                    <monthly-climate-sparkline-chart
                        class="monthly-climate-sparkline"
                        :data="data"
                        :climate-data="props.row.ppt"
                        :line-color="colors.precipitation"
                        unit-of-measurement="mm"
                        value-name="Precip"
                        climate-type="ppt"
                    />
                </b-table-column>
                <b-table-column
                    field="pas"
                    label="Precip. as snow (mm/mo)"
                    centered
                    class="chart-column"
                >
                    <template v-slot:header>
                        <div>
                            Precip. as snow<br>
                            <span class="has-text-weight-light has-text-grey">(mm/mo)</span>
                        </div>
                    </template>
                    <monthly-climate-sparkline-chart
                        class="monthly-climate-sparkline"
                        :data="data"
                        :climate-data="props.row.pas"
                        :line-color="colors.snow"
                        unit-of-measurement="mm"
                        value-name="Snow"
                        climate-type="pas"
                    />
                </b-table-column>
                <b-table-column
                    field="tave"
                    label="Temperature (°C)"
                    centered
                    class="chart-column"
                >
                    <template v-slot:header>
                        <div>
                            Temperature<br>
                            <span class="has-text-weight-light has-text-grey">(°C)</span>
                        </div>
                    </template>
                    <monthly-climate-sparkline-chart
                        class="monthly-climate-sparkline"
                        :data="data"
                        :climate-data="props.row.tave"
                        :line-color="colors.temperature"
                        unit-of-measurement="°C"
                        value-name="Temp"
                        climate-type="tave"
                    />
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
import scssVars from '@/assets/scss/_variables.scss';
import MonthlyClimateSparklineChart from '@/components/charts/MonthlyClimateSparklineChart';

export default {
    name: 'HydrologicVariabilityClimateTable',

    components: {
        MonthlyClimateSparklineChart,
    },

    props: {
        data: {
            type: Array,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        watershedName: {
            type: String,
            required: true,
        },

        colorScheme: {
            type: Array,
            required: true,
        },
    },

    computed: {
        colors() {
            return {
                temperature: scssVars.colorClimateTemperatureLine,
                precipitation: scssVars.colorClimatePrecipitationLine,
                snow: scssVars.colorClimateSnowLine,
            };
        },
    },
};
</script>

<style lang="scss">
.hydrologic-variability-climate-table {
    page-break-after: always;
    margin-bottom: 2em;
    $row-height: 17px;

    h2 {
        font-size: 1.6rem;
        font-weight: 600;
    }

    table {
        line-height: $row-height;

        span.is-invisible {
            display: none;
        }

        tr {
            &.row-1 {
                margin-top: 2em;

                .candidate-header {
                    line-height: $row-height + 10;
                }
            }

            div.station-name {
                margin-left: 1em;
            }
        }

        .candidate-number {
            margin-bottom: 0.5em;
            white-space: nowrap;

            .dot {
                width: 0.8em;
                height: 0.8em;
                border-radius: 0.5em;
                display: inline-block;
                margin-right: 0.2em;
            }
        }
    }

    .chart-column {
        width: 15%;

        .monthly-climate-sparkline {
            width: 100%;
        }
    }
}

</style>
